import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SessaoUsuario } from '@erp/shared';
import {
  api,
  definirFilial,
  definirToken,
  quandoSessaoExpirar,
  renovarSessao,
  sair as sairApi,
} from './api';

interface ValorSessao {
  usuario: SessaoUsuario | null;
  carregando: boolean;
  filial: string;
  trocarFilial: (id: string) => void;
  entrou: (token: string, u: SessaoUsuario) => void;
  sair: () => Promise<void>;
  /** Busca a sessão de novo (ex.: depois de trocar de plano, o menu muda na hora). */
  recarregar: () => Promise<void>;
}

const Ctx = createContext<ValorSessao | null>(null);

function aplicarMarca(u: SessaoUsuario | null) {
  const cor = u?.tenant.corPrimaria;
  if (cor) document.documentElement.style.setProperty('--primary', cor);
  else document.documentElement.style.removeProperty('--primary');
}

export function SessaoProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [usuario, setUsuario] = useState<SessaoUsuario | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [filial, setFilial] = useState('todas');

  const limpar = useCallback(() => {
    definirToken(null);
    setUsuario(null);
    aplicarMarca(null);
    qc.clear();
  }, [qc]);

  // Ao abrir: tenta retomar a sessão pelo cookie de refresh.
  useEffect(() => {
    quandoSessaoExpirar(limpar);
    let vivo = true;
    void (async () => {
      if (await renovarSessao()) {
        try {
          const u = await api<SessaoUsuario>('/auth/eu');
          if (vivo) {
            setUsuario(u);
            aplicarMarca(u);
          }
        } catch {
          /* sessão inválida: fica deslogado */
        }
      }
      if (vivo) setCarregando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [limpar]);

  const valor = useMemo<ValorSessao>(
    () => ({
      usuario,
      carregando,
      filial,
      trocarFilial: (id) => {
        definirFilial(id);
        setFilial(id);
        void qc.invalidateQueries();
      },
      entrou: (token, u) => {
        definirToken(token);
        definirFilial('todas');
        setFilial('todas');
        setUsuario(u);
        aplicarMarca(u);
      },
      sair: async () => {
        await sairApi();
        limpar();
      },
      recarregar: async () => {
        const u = await api<SessaoUsuario>('/auth/eu');
        setUsuario(u);
        aplicarMarca(u);
      },
    }),
    [usuario, carregando, filial, qc, limpar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): ValorSessao {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSessao fora do SessaoProvider');
  return v;
}

/** Testes: provider com sessão fixa. */
export function SessaoFixa({ valor, children }: { valor: ValorSessao; children: ReactNode }) {
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}
export type { ValorSessao };
